import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosConfig';

// Problem categories returned by /api/sales-automation/results (by_error).
export const ERROR_TYPE_LABEL = {
    salesman_not_found:     'Salesman not found',
    salesman_wrong_company: 'Salesman not in company',
    rate_limit:             'Too many requests',
    timeout:                'Browser timeout',
    page_crash:             'Page crashed',
    bad_file:               'Bad file (no header row)',
    other:                  'Other',
};

const STAGE_LABEL = {
    problems: 'All problems',
    download: 'Download failed',
    upload:   'Upload failed',
    partial:  'Partial (row errors)',
    all:      'Everything',
};

const STATUS_LABEL = { success: 'Success', partial: 'Partial', failed: 'Failed', skipped: 'Skipped' };
const StatusBadge = ({ status }) => (
    <span className={`sal-badge sal-badge--${status}`}>{STATUS_LABEL[status] ?? status}</span>
);

// Playwright errors carry a multi-line call log; the first line says what failed.
const firstLine = (s) => (s || '').split('\n')[0];

const LIMIT = 50;

const IssueRow = ({ row, canSync, syncDisabled, onResync }) => {
    const [open, setOpen] = useState(false);
    const errors = row.row_errors || [];
    const stageErr = [row.download_error && `Download: ${row.download_error}`,
                      row.upload_error   && `Upload: ${row.upload_error}`].filter(Boolean);
    const hasDetail = errors.length > 0 || stageErr.length > 0;

    return (
        <>
            <tr className="sal-run-row" onClick={() => hasDetail && setOpen(o => !o)}
                style={{ cursor: hasDetail ? 'pointer' : 'default' }}>
                <td>{row.report_date}</td>
                <td><code>{row.company_code}</code></td>
                <td>
                    <StatusBadge status={row.download_status} />
                    {row.download_error && (
                        <div className="sal-issue-err" title={row.download_error}>{firstLine(row.download_error)}</div>
                    )}
                </td>
                <td>
                    <StatusBadge status={row.partial ? 'partial' : row.upload_status} />
                    {row.upload_error && (
                        <div className="sal-issue-err" title={row.upload_error}>{firstLine(row.upload_error)}</div>
                    )}
                </td>
                <td className="sal-counts">
                    {row.salesmen_success ?? '—'} / {row.salesmen_failed ?? '—'}
                    {errors.length > 0 && <span className="sal-issue-count">{errors.length} row error{errors.length > 1 ? 's' : ''}</span>}
                </td>
                <td>
                    <span className={`sal-type-tag sal-type-tag--${row.run_type}`}>{row.run_type}</span>
                    <span className="sal-issue-run">#{row.run_id}</span>
                </td>
                <td onClick={e => e.stopPropagation()}>
                    {canSync && (
                        <button
                            className="sal-btn sal-btn--sm"
                            disabled={syncDisabled}
                            title={syncDisabled ? 'Sync unavailable or already running' : `Re-sync ${row.company_code} for ${row.report_date}`}
                            onClick={() => onResync({
                                from: row.report_date, to: row.report_date, companies: [row.company_code],
                            })}
                        >
                            Resync
                        </button>
                    )}
                </td>
            </tr>
            {open && (
                <tr className="sal-detail-row">
                    <td colSpan={7}>
                        <ul className="sal-issue-list">
                            {stageErr.map((e, i) => <li key={`s${i}`}><pre>{e}</pre></li>)}
                            {errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                    </td>
                </tr>
            )}
        </>
    );
};

const SalesAutomationIssues = ({ canSync, syncDisabled, onResync, refreshKey, today, shiftDate }) => {
    const [company,   setCompany]   = useState('');
    const [stage,     setStage]     = useState('problems');
    const [errorType, setErrorType] = useState('');
    const [fromDate,  setFromDate]  = useState(shiftDate(today, -30));
    const [toDate,    setToDate]    = useState(today);
    const [latest,    setLatest]    = useState(true);
    const [page,      setPage]      = useState(1);
    const [data,      setData]      = useState(null);
    const [loading,   setLoading]   = useState(true);
    const [error,     setError]     = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { stage, latest, page, limit: LIMIT };
            if (company)   params.company    = company;
            if (errorType) params.error_type = errorType;
            if (fromDate)  params.from_date  = fromDate;
            if (toDate)    params.to_date    = toDate;
            const res = await axiosInstance.get('/api/sales-automation/results', { params });
            setData(res.data.data);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load results');
        }
        setLoading(false);
    }, [company, stage, errorType, fromDate, toDate, latest, page]);

    useEffect(() => { load(); }, [load, refreshKey]);

    // Every filter change goes back to page 1.
    const set = (setter) => (value) => { setter(value); setPage(1); };
    const pick = (c, s) => { setCompany(c); setStage(s); setErrorType(''); setPage(1); };
    const clear = () => {
        setCompany(''); setStage('problems'); setErrorType('');
        setFromDate(shiftDate(today, -30)); setToDate(today); setLatest(true); setPage(1);
    };

    const totalPages = data ? Math.ceil(data.total / LIMIT) : 0;
    const companies  = data?.companies ?? [];

    return (
        <div className="sal-issues">
            <div className="sal-issue-filters">
                <label>
                    Company
                    <select className="sal-filter" value={company} onChange={e => set(setCompany)(e.target.value)}>
                        <option value="">All companies</option>
                        {companies.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
                <label>
                    Show
                    <select className="sal-filter" value={stage} onChange={e => set(setStage)(e.target.value)}>
                        {Object.entries(STAGE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </label>
                <label>
                    Error type
                    <select className="sal-filter" value={errorType} onChange={e => set(setErrorType)(e.target.value)}>
                        <option value="">Any error</option>
                        {Object.entries(ERROR_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </label>
                <label>
                    From
                    <input type="date" className="sal-filter" value={fromDate} max={today}
                        onChange={e => set(setFromDate)(e.target.value)} />
                </label>
                <label>
                    To
                    <input type="date" className="sal-filter" value={toDate} max={today}
                        onChange={e => set(setToDate)(e.target.value)} />
                </label>
                <label className="sal-issue-check" title="Hide attempts that a later sync already replaced">
                    <input type="checkbox" checked={latest} onChange={e => set(setLatest)(e.target.checked)} />
                    Latest attempt only
                </label>
                <button className="sal-btn sal-btn--sm" onClick={clear}>Clear</button>
            </div>

            {data && (
                <div className="sal-issue-dash">
                    <div className="sal-issue-matrix">
                        {data.by_company.map(c => (
                            <div key={c.company_code}
                                className={`sal-issue-co ${company === c.company_code ? 'sal-issue-co--active' : ''}`}>
                                <button className="sal-issue-co__name"
                                    onClick={() => pick(company === c.company_code ? '' : c.company_code, 'problems')}>
                                    {c.company_code}
                                </button>
                                <div className="sal-issue-co__stats">
                                    <button className="sal-issue-stat sal-issue-stat--failed"
                                        onClick={() => pick(c.company_code, 'download')}>
                                        <b>{c.download_failed}</b> download
                                    </button>
                                    <button className="sal-issue-stat sal-issue-stat--failed"
                                        onClick={() => pick(c.company_code, 'upload')}>
                                        <b>{c.upload_failed}</b> upload
                                    </button>
                                    <button className="sal-issue-stat sal-issue-stat--partial"
                                        onClick={() => pick(c.company_code, 'partial')}>
                                        <b>{c.partial}</b> partial
                                    </button>
                                </div>
                                <span className="sal-issue-co__sub">{c.total} report-days checked</span>
                            </div>
                        ))}
                    </div>

                    <div className="sal-issue-chips">
                        {Object.entries(ERROR_TYPE_LABEL).map(([k, label]) => {
                            const n = data.by_error?.[k] ?? 0;
                            if (!n && errorType !== k) return null;
                            return (
                                <button key={k}
                                    className={`sal-chip ${errorType === k ? 'sal-chip--active' : ''}`}
                                    onClick={() => set(setErrorType)(errorType === k ? '' : k)}>
                                    {label} <b>{n}</b>
                                </button>
                            );
                        })}
                    </div>

                    {data.top_errors?.length > 0 && (
                        <details className="sal-issue-top">
                            <summary>Most frequent row errors ({data.top_errors.length})</summary>
                            <table className="sal-detail-table">
                                <thead>
                                    <tr><th>Error</th><th>Times</th><th>Companies</th><th>Last date</th></tr>
                                </thead>
                                <tbody>
                                    {data.top_errors.map(t => (
                                        <tr key={t.message}>
                                            <td>{t.message}</td>
                                            <td className="sal-counts">{t.count}</td>
                                            <td>{t.companies.join(', ')}</td>
                                            <td>{t.last_date}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </details>
                    )}
                </div>
            )}

            {error && <div className="sal-error">{error}</div>}

            <div className="sal-issue-bar">
                <span>
                    {loading ? 'Loading…' : `${data?.total ?? 0} row${data?.total === 1 ? '' : 's'}`}
                    {!loading && data?.min_date && ` · ${data.min_date} → ${data.max_date}`}
                </span>
                {canSync && data?.total > 0 && (
                    <button
                        className="sal-btn sal-btn--sm sal-btn--primary"
                        disabled={syncDisabled}
                        title="Open Manual Sync for the company and date span of these rows"
                        onClick={() => onResync({
                            from: data.min_date, to: data.max_date,
                            companies: company ? [company] : null,
                        })}
                    >
                        Resync these
                    </button>
                )}
            </div>

            <div className="sal-table-wrap">
                <table className="sal-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Company</th>
                            <th>Download</th>
                            <th>Upload</th>
                            <th>Salesmen (OK / Failed)</th>
                            <th>Run</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {!loading && data?.data.length === 0
                            ? <tr><td colSpan={7} className="sal-empty">No matching rows — nothing to fix here</td></tr>
                            : data?.data.map(r => (
                                <IssueRow key={r.id} row={r} canSync={canSync}
                                    syncDisabled={syncDisabled} onResync={onResync} />
                            ))
                        }
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="sal-pagination">
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                    <span>{page} / {totalPages}</span>
                    <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
            )}
        </div>
    );
};

export default SalesAutomationIssues;
